
import { Box, Modal, TextField, Button } from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../../../app/context/useApp";
import AppAlert from "../../../ui/components/AppAlert";
import { getAllActiveSilos, createSilos } from "../../../core/services/silos.service";
import { mobileNavMediaQuery } from "../../../layout/layoutMetrics";
import {
  dispatchExpandConversations,
  dispatchExpandProjects,
} from "../../../layout/sidebarAccordionEvents";

const MODE_ROUTES = Object.freeze({
  Analyze: "/analyze",
  Brief: "/brief",
  Generate: "/generate",
  Audience: "/audience",
  Activate: "/activate",
  Home: "/",
});

const SILO_ROUTES = Object.values(MODE_ROUTES).filter((route) => route !== "/");

const GREY_MODES = new Set(["Audience", "Activate"]);

const getClientId = (client) =>
  client?.clientId ?? client?.Id ?? client?.id ?? null;

export default function ModeButtons({ hideBrief = false }) {
  const { 
    selectedClient, 
    setActiveMode, 
    activeMode,
    fetchConversationsBySilo,
    clearConversations,
    clearActiveChatSession,
    createNewChat,
    currentChatSession,
    messages,
    selectedSiloIsDefault,
    selectedSilo,
    loadingConversations,
  } = useApp();
  const [activeSilos, setActiveSilos] = useState([]);
  const toBool = (value) => value === true || value === "true" || value === 1 || value === "1";

  const navigate = useNavigate();
  const location = useLocation();

  const [alert, setAlert] = useState({
    open: false,
    title: "",
    message: "",
    isComingSoon: false,
  });

  // const [modalOpen, setModalOpen] = useState(false);
  // const [silosName, setSilosName] = useState("");

  const openAlert = (title, message, isComingSoon = false) =>
    setAlert({ open: true, title, message, isComingSoon });

  const path = location.pathname;

  const getRouteForMode = (mode) => MODE_ROUTES[mode] ?? null;

  const isSiloRoute = (pathname) =>
    SILO_ROUTES.some((route) =>
      pathname.startsWith(route),
    );


  // let modes =
  //   ["Brief", "Analyze", "Generate", "Audience", "Activate"];


  const handleSiloClick = async (silo) => {
    const targetRoute = getRouteForMode(silo?.silosName);
    const leavingWorkroom = path.startsWith("/project/");
    if (leavingWorkroom) {
      // Keep Projects + Conversation expanded when entering a silo from workroom.
      dispatchExpandProjects();
      dispatchExpandConversations();
    }
    const hasActiveHomeChat =
      !!currentChatSession &&
      (Array.isArray(messages) && messages.length > 0
        ? true
        : !!(
            currentChatSession?.conversationsId ||
            currentChatSession?.conversationId ||
            (Array.isArray(currentChatSession?.messages) &&
              currentChatSession.messages.length > 0)
          ));
    const shouldStartFreshSiloChat =
      !!targetRoute &&
      targetRoute !== "/" &&
      selectedSiloIsDefault &&
      hasActiveHomeChat;

    // Clear previous conversations before loading new ones
    clearConversations();
    
    // Fetch conversations for this silo
    await fetchConversationsBySilo(silo.silosId, {
      isDefaultSelected: toBool(silo.defaultSelected ?? silo.defaultselected),
      resetChatType: true,
      forceClientScoped: silo.silosName === "Generate",
    });
    
    // Handle navigation like before
    await handleAction(silo.silosName);

    if (shouldStartFreshSiloChat) {
      clearActiveChatSession();
      createNewChat(selectedClient);
    }
  };

  

  const handleAction = async (mode) => {
    const targetRoute = getRouteForMode(mode);
    const shouldResetCrossSiloChat =
      !!targetRoute &&
      isSiloRoute(path) &&
      isSiloRoute(targetRoute) &&
      !path.startsWith(targetRoute);

    if (shouldResetCrossSiloChat) {
      clearActiveChatSession();
    }

    if (mode === "Home") {
      setActiveMode(null);
      navigate("/");
      return;
    }

    if (mode === "Analyze") {
      setActiveMode("Analyze");
      navigate("/analyze");
      return;
    }

    if (mode === "Brief") {
      setActiveMode("Brief");
      navigate("/brief");
      return;
    }

    if (mode === "Generate") {
      setActiveMode("Generate");
      navigate("/generate");
      return;
    }

    if (mode === "Audience") {
      setActiveMode("Audience");
      navigate("/audience");
      return;
    }

    if (mode === "Activate") {
      setActiveMode("Activate");
      navigate("/activate");
      return;
    }
  };

 
  const getActiveMode = () => {
    if (path.includes("analyze")) return "Analyze";
    if (path.includes("brief")) return "Brief";
    if (path.includes("generate")) return "Generate";
    if (path.includes("audience")) return "Audience";
    if (path.includes("activate")) return "Activate";
    return null;
  };

  const currentActive = getActiveMode();

  const hasSelectedClient = !!getClientId(selectedClient);

  // const handleCreateSilos = async () => {
  //   if (!silosName.trim()) {
  //     openAlert("Validation Error", "Please enter a silos name");
  //     return;
  //   }

  //   try {
  //     await createSilos({ silosName: silosName.trim() });
  //     setSilosName("");
  //     setModalOpen(false);
  //     openAlert("Success", "Silos created successfully");
      
  //     // Refresh the silos list
  //     const response = await getAllActiveSilos();
  //     setActiveSilos(response.data);
  //   } catch (error) {
  //     openAlert("Error", error.message || "Failed to create silos");
  //   }
  // };

  // const handleCancelCreate = () => {
  //   setSilosName("");
  //   setModalOpen(false);
  // };

  useEffect(() => {
    getAllActiveSilos().then((response) => {
      const visibleSilos = (response.data || []).filter((silo) => {
        const hiddenFlag = silo.isHide ?? silo.ishide ?? silo.is_hidden;
        return !toBool(hiddenFlag);
      });

      const defaultSilo = visibleSilos.find((silo) =>
        toBool(silo.defaultSelected ?? silo.defaultselected)
      );

      // Default-selected silo should work in background but stay hidden in UI.
      const uiSilos = visibleSilos.filter((silo) =>
        !toBool(silo.defaultSelected ?? silo.defaultselected)
      );

      // Define the correct order
      const correctOrder = ["Analyze", "Brief", "Generate", "Audience", "Activate"];
      
      // Sort the silos according to the correct order
      const sortedSilos = [...uiSilos].sort((a, b) => {
        const aIndex = correctOrder.indexOf(a.silosName);
        const bIndex = correctOrder.indexOf(b.silosName);
        
        // If both are in the correct order array, sort by their position
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        
        // If only one is in the correct order array, put it first
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        
        // If neither is in the correct order array, maintain alphabetical order
        return a.silosName.localeCompare(b.silosName);
      });
      
      setActiveSilos(sortedSilos);

      if (!selectedSilo && defaultSilo?.silosId) {
        fetchConversationsBySilo(defaultSilo.silosId, {
          isDefaultSelected: true,
        });
      }
    });
  }, []);


  return (
    <>
      {hasSelectedClient ? (
        <Box
          sx={{
            display: "flex",
            gap: { xs: 1, sm: 1.25, md: 1.5, lg: 2 },
            justifyContent: "center",
            flexWrap: "wrap",
            flexShrink: 0,
            mt: { xs: 1, sm: 1.5, md: 2 },
            px: { xs: 1, sm: 0 },
            [`@media ${mobileNavMediaQuery}`]: {
              gap: 0.75,
              flexWrap: "nowrap",
              alignItems: "center",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              mt: 0.75,
              px: 0.5,
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              boxSizing: "border-box",
            },
          }}
        >
          {activeSilos
            .filter((silo) => !(hideBrief && silo.silosName === "Brief"))
            .map((silo) => {
            const isSelectedSilo =
              String(selectedSilo ?? "") === String(silo.silosId ?? "");
            const isActive = isSelectedSilo || silo.silosName === currentActive;
            const isGreyMode = GREY_MODES.has(silo.silosName);

            return (
              <Box
                key={silo.silosId ?? silo.silosName}
                aria-pressed={isActive}
                onClick={() => handleSiloClick(silo)}
                sx={{
                  width: { xs: 56, sm: 58, md: 60 },
                  height: { xs: 56, sm: 58, md: 60 },
                  px: { xs: 1.25, sm: 1.6, md: 2.2 },
                  py: 0,
                  borderRadius: "1000px",
                  cursor: "pointer",
                  position: "relative",
                  zIndex: 6,
                  fontSize: { xs: 11, sm: 12, md: 13 },
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                  letterSpacing: "0.01em",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: isActive
                    ? isGreyMode
                      ? "0.2px solid rgba(180,180,180,0.55)"
                      : "0.2px solid rgba(255,255,255,0.7)"
                    : "1px solid transparent",
                  backdropFilter: "blur(14px) saturate(125%)",
                  WebkitBackdropFilter: "blur(14px) saturate(125%)",
                  transition: "all 0.24s ease",
                  background: isGreyMode
                    ? isActive
                      ? "linear-gradient(135deg, rgba(140,140,140,0.42), rgba(100,100,100,0.28))"
                      : "linear-gradient(135deg, rgba(120,120,120,0.3), rgba(90,90,90,0.16))"
                    : isActive
                      ? "linear-gradient(135deg, rgba(255,255,255,0.36), rgba(255,255,255,0.2))"
                      : "linear-gradient(135deg, rgba(255,255,255,0.24), rgba(255,255,255,0.1))",

                  color: isGreyMode
                    ? "#000000"
                    : "rgba(255,255,255,0.96)",
                  fontWeight: isActive ? "600" : "500",
                  boxShadow: isGreyMode
                    ? isActive
                      ? "inset 0 1px 0 rgba(180,180,180,0.28), 0 0 0 4px rgba(120,120,120,0.1), 0 10px 20px rgba(0,0,0,0.2)"
                      : "inset 0 1px 0 rgba(160,160,160,0.18), 0 6px 12px rgba(0,0,0,0.12)"
                    : isActive
                      ? "inset 0 1px 0 rgba(255,255,255,0.45), 0 0 0 4px rgba(255,255,255,0.12), 0 14px 28px rgba(0,0,0,0.26)"
                      : "inset 0 1px 0 rgba(255,255,255,0.24), 0 8px 16px rgba(0,0,0,0.15)",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    borderRadius: "1000px",
                    filter: "blur(10px)",
                    zIndex: -1,
                    opacity: isActive ? 0.95 : 0.75,
                    transition: "all 0.24s ease",
                  },

                  "&:hover": {
                    background: isGreyMode
                      ? isActive
                        ? "linear-gradient(135deg, rgba(150,150,150,0.46), rgba(110,110,110,0.32))"
                        : "linear-gradient(135deg, rgba(130,130,130,0.36), rgba(100,100,100,0.22))"
                      : isActive
                        ? "linear-gradient(135deg, rgba(255,255,255,0.4), rgba(255,255,255,0.24))"
                        : "linear-gradient(135deg, rgba(255,255,255,0.3), rgba(255,255,255,0.15))",
                    transform: "translateY(-4px)",
                    "&::after": {
                      opacity: 0.9,
                    },
                  },
                  [`@media ${mobileNavMediaQuery}`]: {
                    flex: "1 1 0",
                    width: "auto",
                    minWidth: 0,
                    height: 48,
                    px: 0.35,
                    fontSize: 10,
                    letterSpacing: 0,
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxShadow: isGreyMode
                      ? isActive
                        ? "inset 0 1px 0 rgba(180,180,180,0.28), 0 0 0 2px rgba(120,120,120,0.12)"
                        : "inset 0 1px 0 rgba(160,160,160,0.18)"
                      : isActive
                        ? "inset 0 1px 0 rgba(255,255,255,0.45), 0 0 0 2px rgba(255,255,255,0.14)"
                        : "inset 0 1px 0 rgba(255,255,255,0.24)",
                    "&:hover": {
                      transform: "none",
                    },
                  },
                }}
              >
                {silo.silosName}
                {loadingConversations && isSelectedSilo && (
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      width: 12,
                      height: 12,
                      display: 'inline-block',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                      animation: 'spin 1s linear infinite',
                      '&::before': {
                        content: '"⏳"',
                        fontSize: 10,
                      },
                    }}
                  />
                )}
              </Box>
            );
          })}
          
          {/* Plus Button - Commented out */}
          {/* <Box
            onClick={() => setModalOpen(true)}
            sx={{
              px: 3.2,
              py: 1.1,
        color: "white",
              borderRadius: "24px",
              cursor: "pointer",
              backdropFilter: "blur(6px)",
              // transition: "all 0.2s ease",
              background: "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.1))",
              fontWeight: "400",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "60px",
              fontSize: "20px",

              "&:hover": {
                background: "linear-gradient(135deg, rgba(255,255,255,0.3), rgba(255,255,255,0.15))",
                transform: "translateY(-2px)",
              },
            }}
          /> */}
        </Box>
      ) : null}

      <AppAlert
        open={alert.open}
        title={alert.title}
        message={alert.message}
        isComingSoon={alert.isComingSoon}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      {/* Create Silos Modal - Commented out */}
      {/* <Modal
        open={modalOpen}
        onClose={handleCancelCreate}
        aria-labelledby="create-silos-modal"
        aria-describedby="create-silos-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <h2 id="create-silos-modal">Create New Silos</h2>
          
          <TextField
            fullWidth
            label="Silos Name"
            variant="outlined"
            value={silosName}
            onChange={(e) => setSilosName(e.target.value)}
            margin="normal"
            placeholder="Enter silos name"
          />
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleCancelCreate}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateSilos}
              disabled={!silosName.trim()}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Modal> */}
    </>
  );
}